import {
  useCallback,
  useState,
  type ChangeEvent,
  type Dispatch,
  type KeyboardEvent,
  type SetStateAction,
} from "react";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { TOptions } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

import type {
  IncidentWorkItem,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import { getWorkflowTranslationContext, tk } from "@thalamos/vision-common";

import { Banner } from "../../components/Banner/Banner.js";
import { TypographyI18N } from "../../components/index.js";

export type ShareModalProps = {
  emails: string[];
  setEmails: Dispatch<SetStateAction<string[]>>;
  submitting: boolean;
  activePlaceOfSafetyName: string | undefined;
  hasPosEmails: boolean;
  setMaybeEmail: Dispatch<SetStateAction<string>>;
  maybeEmail: string;
  error: string | undefined;
  setError: Dispatch<SetStateAction<string | undefined>>;
  incidentWorkItem?: IncidentWorkItemWithContext;
};

export type SharePageWithoutEmailProps = {
  activePlaceOfSafetyName: string | undefined;
  aspireFrontendUrl: IncidentWorkItem["aspireFrontendUrl"];
  shareCode: IncidentWorkItem["shareCode"] | undefined;
  incidentWorkItem: IncidentWorkItemWithContext;
  checkboxChecked?: boolean;
  onCheckboxChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  checkboxError?: string;
};

export function SharePageWithEmail({
  emails,
  setEmails,
  hasPosEmails,
  activePlaceOfSafetyName,
  setMaybeEmail,
  maybeEmail,
  setError,
  error,
  incidentWorkItem,
}: ShareModalProps) {
  const theme = useTheme();

  const { t } = useTranslation();
  const translationOptions = {
    context: getWorkflowTranslationContext({ incidentWorkItem }),
  };

  const [addEmailEnabled, setAddEmailEnabled] = useState(false);
  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      setMaybeEmail(input);

      const parsedEmail = z.string().trim().pipe(z.email()).safeParse(input);

      if (parsedEmail.success) {
        setError(undefined);
      }

      setAddEmailEnabled(parsedEmail.success);
    },
    [setEmails, emails, setMaybeEmail],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const parsedEmail = z
        .string()
        .trim()
        .pipe(z.email())
        .safeParse(maybeEmail);
      if (parsedEmail.success) {
        setEmails([...emails, parsedEmail.data.trim()]);
        setMaybeEmail("");
        setError(undefined);
        setAddEmailEnabled(false);
      } else {
        setError("Invalid email address");
      }
    }
  };

  return (
    <Stack
      sx={{
        gap: "2rem",
        my: 2,
      }}
    >
      <Stack
        sx={{
          gap: "1rem",
        }}
      >
        <TypographyI18N translationKey={tk("sharePage.shareWith")} />
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          {activePlaceOfSafetyName ?? t("common.unknown")}
        </Typography>

        <Box
          sx={{
            border: "1px solid",
            borderColor: theme.palette.text.disabled,
            borderRadius: theme.spacing(2),
            padding: theme.spacing(2),
          }}
        >
          <TypographyI18N
            translationKey={
              emails.length > 0
                ? tk("sharePage.emailSentTo")
                : tk("sharePage.addEmail")
            }
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(1),
            }}
          />
          {emails.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                paddingBottom: "1em",
                flexWrap: "wrap",
                gap: theme.spacing(1),
              }}
            >
              {emails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  variant="outlined"
                  onDelete={() => {
                    setEmails(emails.filter((_, i) => i !== index));
                  }}
                  color="primary"
                  sx={{
                    marginLeft: "0 !important",
                  }}
                />
              ))}
            </Stack>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(1),
            }}
          >
            <TextField
              placeholder={t("sharePage.sharingWithPlaceholder")}
              fullWidth
              value={maybeEmail}
              onChange={handleEmailChange}
              error={error !== undefined}
              helperText={error}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  autoComplete: "off",
                  inputProps: {
                    "data-testid": `formField-email-input`,
                  },
                },
              }}
            />
            <IconButton
              disabled={!addEmailEnabled}
              sx={{
                backgroundColor: theme.palette.common.teal,
                color: theme.palette.common.white,
                borderRadius: "50%",
                height: "35px",
                width: "35px",
                "&:hover": {
                  backgroundColor: theme.palette.common.deepTeal,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.grey[400],
                },
              }}
              onClick={() => {
                const parsedEmail = z
                  .string()
                  .trim()
                  .pipe(z.email())
                  .safeParse(maybeEmail);
                if (parsedEmail.success) {
                  setEmails([...emails, parsedEmail.data.trim()]);
                  setMaybeEmail("");
                  setError(undefined);
                  setAddEmailEnabled(false);
                } else {
                  setError("Invalid email address");
                }
              }}
            >
              <Add />
            </IconButton>
          </Box>
        </Box>
        {hasPosEmails && emails.length > 0 && (
          <Banner
            bannerType={"info"}
            body={{
              translationKey: tk("sharePage.infoBannerMessage"),
              translationOptions,
            }}
          />
        )}
      </Stack>
    </Stack>
  );
}

export function SharePageWithoutEmail(props: SharePageWithoutEmailProps) {
  const theme = useTheme();

  const { t } = useTranslation();
  const tOptions = {
    context: getWorkflowTranslationContext({
      incidentWorkItem: props.incidentWorkItem,
    }),
  };

  return (
    <Stack
      sx={{
        gap: "2rem",
        my: 2,
      }}
    >
      <Stack
        sx={{
          gap: "1rem",
        }}
      >
        <TypographyI18N
          translationKey={tk("sharePage.shareWith")}
          sx={{
            fontWeight: 400,
          }}
        />
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          {props.activePlaceOfSafetyName ?? t("common.unknown")}
        </Typography>

        <TypographyI18N
          translationKey={tk("sharePage.shareWithoutEmail.posWithoutEmail")}
          translationOptions={tOptions}
          sx={{
            fontWeight: 400,
          }}
        />
        <Typography
          sx={{
            fontWeight: 400,
          }}
        >
          {t("sharePage.shareWithoutEmail.healthServices", tOptions)}
          <br />
          <Box
            component="span"
            sx={{ fontWeight: "bold", wordBreak: "break-all" }}
          >
            {props.aspireFrontendUrl}/mhf
          </Box>
        </Typography>

        <Typography
          component="div"
          sx={{
            fontWeight: 400,
          }}
        >
          {t("sharePage.shareWithoutEmail.incidentCode")}
          <br />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: theme.spacing(1),
            }}
          >
            <Box
              sx={{
                fontWeight: "bold",
                border: "1px solid",
                borderColor: theme.palette.secondary.dark,
                borderRadius: "8px",
                padding: theme.spacing(1.5, 3),
                fontSize: "1.5rem",
                textAlign: "center",
                minWidth: "12.5rem",
                letterSpacing: "0.5rem",
              }}
            >
              {props.shareCode}
            </Box>
          </Box>
        </Typography>

        <Typography
          sx={{
            fontWeight: 400,
          }}
        >
          {t("sharePage.shareWithoutEmail.healthConfirmEmail", tOptions)}
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
          }}
        >
          <Trans
            i18nKey="sharePage.shareWithoutEmail.guestUsers"
            components={{
              1: (
                <a
                  href="https://thalamos1.zohodesk.eu/portal/en/kb/mh-forms-quick-reference-guides"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.palette.primary.main }}
                />
              ),
            }}
          />
        </Typography>
        {props.checkboxChecked !== undefined && props.onCheckboxChange && (
          <FormControl error={!!props.checkboxError}>
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="checkbox-form-review"
                  checked={props.checkboxChecked}
                  onChange={props.onCheckboxChange}
                  sx={{ ml: 1 }}
                />
              }
              label={t(
                "sharePage.shareWithoutEmail.healthServicesCheckboxLabel",
                tOptions,
              )}
            />
            {props.checkboxError && (
              <FormHelperText>{props.checkboxError}</FormHelperText>
            )}
          </FormControl>
        )}
      </Stack>
    </Stack>
  );
}

export function UnableToShareWithPos({
  translationOptions,
}: {
  translationOptions: TOptions;
}) {
  return (
    <Stack
      sx={{
        gap: "2rem",
        my: 2,
      }}
    >
      <Banner
        body={[
          tk("sharePage.unableToShare.messageOne"),
          tk("sharePage.unableToShare.messageTwo"),
          tk("sharePage.unableToShare.messageThree"),
        ].map((key) => ({ translationKey: key, translationOptions }))}
        bannerType={"error"}
      />
    </Stack>
  );
}
